import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Divider,
  Stack,
} from '@mui/material'

import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

import { AuthorAndDateInfo } from '@components/AuthorAndDateInfo'
import { TLGLink } from '@components/TLGLink'
import { TLGTypography } from '@components/TLGTypography'

import { BlogPostType, IBlogPost } from '@utils/types'

import {
  blogPostAuthorAndDateSx,
  cardActionsSx,
  cardContentSx,
  cardSx,
  previewSx,
  titleSx,
} from './styles'

export interface IBlogPostProps
  extends Pick<
    IBlogPost,
    'title' | 'preview' | 'image' | 'slug' | 'createdByAdmin' | 'publishedDate'
  > {
  postType: BlogPostType
}

const BlogPost = ({
  postType,
  title,
  createdByAdmin,
  publishedDate,
  preview,
  image,
  slug,
}: IBlogPostProps) => {
  const { t } = useTranslation()

  const blogPostLink = `/blog/${postType}/${slug}`

  return (
    <TLGLink data-testid="blog-post-link" to={blogPostLink} underline="none">
      <Card sx={cardSx} data-testid="blog-post">
        <CardMedia
          data-testid="blog-post-image"
          component="img"
          loading="lazy"
          alt={t('Blog post cover image')}
          image={image as string}
        />
        <CardContent sx={cardContentSx}>
          <TLGTypography
            data-testid="blog-post-title"
            component="h2"
            responsiveVariant={{ xs: 'h5', md: 'h4' }}
            title={title}
            sx={titleSx}
          >
            {title}
          </TLGTypography>

          <Stack>
            <Divider />

            <TLGTypography
              data-testid="blog-post-author-and-date"
              variant="subtitle1"
              title={t('Publication Info')}
              sx={blogPostAuthorAndDateSx}
            >
              <AuthorAndDateInfo
                author={createdByAdmin}
                publishedDate={publishedDate}
              />
            </TLGTypography>
          </Stack>

          <TLGTypography
            data-testid="blog-post-preview"
            title={preview}
            sx={previewSx}
          >
            {preview}
          </TLGTypography>
        </CardContent>
        <CardActions sx={cardActionsSx}>
          <Button
            component={RouterLink}
            data-testid="blog-post-read-article-button"
            variant="outlined"
            size="medium"
            endIcon={<ArrowForwardIcon />}
            to={blogPostLink}
          >
            {t('READ MORE')}
          </Button>
        </CardActions>
      </Card>
    </TLGLink>
  )
}

export { BlogPost }
